import React, { useState, useEffect, useRef } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { postData, getData, putData } from "../../../API";
import { ToastContainer, toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import * as XLSX from "xlsx";
import { Formik } from "formik";
import * as Yup from "yup";

function SubCategory() {
  // State variables
  const [allData, setAllData] = useState([]); // Store all data from API
  const [filteredData, setFilteredData] = useState([]); // Store filtered data for display
  const [show, setShow] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [categoriesList, setCategoryList] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10); // Fixed page size
  const [totalPages, setTotalPages] = useState(0);
  const fileInputRef = useRef(null);
  const [filters, setFilters] = useState({
    categoryName: "",
  });

  // Modal handling functions
  const handleClose = () => {
    setShow(false);
    setSelectedSubcategory(null);
  };

  const handleShow = () => setShow(true);

  // Form validation schema with Yup
  const validationSchema = Yup.object().shape({
    categoryId: Yup.string().required("Category is required"),
    subCategoryName: Yup.string()
      .required("Subcategory name is required")
      .min(3, "Subcategory name should be at least 3 characters"),
    rate: Yup.number()
      .required("Rate is required")
      .typeError("Rate must be a number")
      .positive("Rate must be greater than zero"),
    isActive: Yup.boolean(),
  });

  // Initial form values for Formik
  const getInitialValues = () => {
    if (selectedSubcategory) {
      return {
        categoryId: selectedSubcategory.categoryId,
        subCategoryName: selectedSubcategory.subCategoryName,
        rate: selectedSubcategory.rateVal,
        isActive: selectedSubcategory.isActive,
      };
    }
    return {
      categoryId: "",
      subCategoryName: "",
      rate: "",
      isActive: true, // Default to active
    };
  };

  // Form submission handler for subcategory
  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      let subcategoryData = {
        categoryId: values.categoryId,
        subCategoryName: values.subCategoryName,
        rateVal: parseFloat(values.rate),
        isActive: values.isActive,
      };

      if (selectedSubcategory) {
        // Add rsctId only during update
        subcategoryData.rsctId = selectedSubcategory.rsctId;

        // Update existing subcategory
        const res = await putData(
          "RateSubCategory/UpdateCategories",
          subcategoryData
        );

        if (res && res.statusCode === 200) {
          toast.success("Subcategory updated successfully", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          fetchDetails();
          setShow(false);
          setSelectedSubcategory(null);
          resetForm();
        } else if (res && res.statusCode === 404) {
          setMessageError(res.message);
          toast.error(res.message || "Failed to update subcategory", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } else {
        // Add new subcategory (without rsctId)
        const res = await postData(
          "RateSubCategory/AddRateSubCategory",
          subcategoryData
        );

        if (res && res.statusCode === 200) {
          toast.success("Subcategory added successfully", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          fetchDetails();
          setShow(false);
          resetForm();
        } else if (res && res.statusCode === 404) {
          setMessageError(res.message);
          toast.error(res.message || "Failed to add subcategory", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      }
    } catch (error) {
      console.log("Error handling subcategory:", error.message);
      toast.error(
        `Error ${selectedSubcategory ? "updating" : "adding"} subcategory`,
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit functionality
  const handleEdit = (data) => {
    setSelectedSubcategory(data);
    setShow(true);
  };

  const BASE_URL = process.env.REACT_APP_API_BASEURL;

  const handleBulkUpload = async (event) => {
    if (!event || !event.target) {
      console.error("Event or event target is undefined");
      return;
    }
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      for (const [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }
      const token = localStorage.getItem("token");
      try {
        // setIsLoading(true);
        // console.log("Sending request to API...");
        const res = await fetch(BASE_URL + `RateSubCategory/BulkUpload`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });
        if (res.status === 409) {
          const errorResponse = await res.json();
          // console.log("409 Error:", errorResponse.message);
          toast.error(errorResponse.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
        if (res.status === 200) {
          const result = await res.json();
          // console.log("File uploaded successfully", result);
          toast.success("File uploaded successfully", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          const errorResponse = await res.text();
          console.log(errorResponse);
          console.error("File upload failed:", errorResponse);
          toast.error(errorResponse, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      } catch (error) {
        toast.error("An error occurred while uploading the file", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      } finally {
        // setIsLoading(false);
      }
    } else {
      toast.error("Please select a file to upload", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
      });
    }
  };

  // Data fetching functions
  const fetchDetails = async (page = 1) => {
    try {
      const res = await getData(
        `RateSubCategory/GetSubCategories?pageNumber=${page}&pageSize=${pageSize}`
      );

      if (res && res.items) {
        setAllData(res.items);
        setFilteredData(res.items);
        setPageNumber(res.pageNumber);
        setTotalPages(res.totalPages);
      }
    } catch (error) {
      console.log(error, "error from handling");
      toast.error("Error fetching subcategories", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    fetchDetails(pageNumber);
    fetchCategories();
  }, [pageNumber]);

  const fetchCategories = async () => {
    try {
      let res = await getData("RateCategory/GetCategories");
      setCategoryList(res.items);
    } catch (error) {
      console.log(error, "error fetching categories");
      toast.error("Error fetching categories", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    fetchDetails();
    fetchCategories();
  }, []);

  // Client-side filtering implementation
  const handleSearch = () => {
    // If no filter is selected, show all data
    if (!filters.categoryName) {
      setFilteredData(allData);
      return;
    }

    // Filter data based on selected category name
    const filtered = allData.filter(
      (item) => item.categoryName === filters.categoryName
    );

    setFilteredData(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  // Excel download functionality
  const downloadExcel = () => {
    const tableHeaders = ["S.No","Category ID" ,"Category Name", "SubCategory ID", "Subcategory Name", "Rate", "Status"];

    const tableData = filteredData.map((item, index) => ({
      "S.No": index + 1,
      categoryId:item.rsctId,
      Category: item.categoryName,
      SubCategoryId:item.categoryId,
      Subcategory: item.subCategoryName,
      Rate: item.rateVal,
      Status: item.isActive ? "Active" : "Inactive",
    }));

    const worksheet = XLSX.utils.json_to_sheet(tableData);
    XLSX.utils.sheet_add_aoa(worksheet, [tableHeaders], { origin: "A1" });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subcategories");
    XLSX.writeFile(workbook, "SubcategoryData.xlsx");
  };

  // Column definitions for the grid
  const columnDefs = [
    {
      headerName: "S.No",
      field: "sNo",
      valueGetter: (params) => params.node.rowIndex + 1,
      headerClass: "grid-header",
    },
    {
      field: "categoryName",
      headerName: "Category",
      headerClass: "grid-header",
    },
    {
      field: "subCategoryName",
      headerName: "Sub Category",
      headerClass: "grid-header",
    },
    {
      field: "rateVal",
      headerName: "Rate",
      headerClass: "grid-header",
    },
    {
      field: "isActive",
      headerName: "Status",
      cellRenderer: (params) => (params.value ? "Active" : "Inactive"),
      headerClass: "grid-header",
    },
    {
      field: "action",
      headerName: "Action",
      cellRenderer: (params) => {
        return (
          <button
            onClick={() => handleEdit(params.data)}
            className="btn btn-sm rounded-3"
            style={{
              boxShadow:
                "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
              color: "white",
              background: "#9CA3AF",
              fontWeight: 600,
            }}
          >
            Edit
          </button>
        );
      },
      headerClass: "grid-header",
      cellClass: "no-right-border",
      suppressMenu: true,
      sortable: false,
    },
  ];

  const defaultColDef = {
    sortable: true,
    resizable: true,
    flex: 1,
  };
  const handleButtonClick = () => {
    fileInputRef.current.click();
  };
  return (
    <>
      {/* Add/Edit Subcategory Modal */}
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedSubcategory ? "Edit Subcategory" : "Add Subcategory"}
          </Modal.Title>
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                <div className="row filters ps-4 pt-3 pb-3 pe-4">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Category Name<span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Select
                        name="categoryId"
                        style={{
                          boxShadow:
                            "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                        }}
                        value={values.categoryId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.categoryId && !!errors.categoryId}
                      >
                        <option value="">Select Category</option>
                        {categoriesList
                        .filter((item) => item.isActive)
                        .map((item) => (
                          <option value={item.rtId} key={item.rtId}>
                            {item.categoryName}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.categoryId}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>

                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Subcategory Name <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="subCategoryName"
                        style={{
                          boxShadow:
                            "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                        }}
                        type="text"
                        value={values.subCategoryName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.subCategoryName && !!errors.subCategoryName
                        }
                        placeholder="Enter subcategory name"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.subCategoryName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>

                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Rate <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="rate"
                        style={{
                          boxShadow:
                            "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                        }}
                        type="text"
                        value={values.rate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.rate && !!errors.rate}
                        placeholder="Enter rate"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.rate}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>

                  {/* Status Radio Buttons */}
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <div className="d-flex">
                        <Form.Check
                          type="radio"
                          id="status-active"
                          label="Active"
                          name="isActive"
                          checked={values.isActive === true}
                          onChange={() => setFieldValue("isActive", true)}
                          className="me-3"
                        />
                        <Form.Check
                          type="radio"
                          id="status-inactive"
                          label="Inactive"
                          name="isActive"
                          checked={values.isActive === false}
                          onChange={() => setFieldValue("isActive", false)}
                        />
                      </div>
                    </Form.Group>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    boxShadow:
                      "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                  }}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : selectedSubcategory
                    ? "Update"
                    : "Submit"}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Main Content */}
      <main className="page-content content">
        <div className="container-fluid p-0">
          <div className="row align-items-center mb-3">
            <div className="col-md-6">
              <h1 className="title_pages">Subcategory</h1>
            </div>
            <div className="col-md-6 text-end">
              <Button
                variant="primary"
                onClick={handleShow}
                style={{
                  background: "#a7252ced",
                  border: "none",
                  boxShadow:
                    "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                }}
              >
                <i className="fa-solid fa-plus"></i> Add Subcategory
              </Button>
            </div>
          </div>

          <div className="row align-items-end">
            <div className="col-md-3 mb-3">
              <div className="form-group">
                <label>Name of the Category</label>
                <select
                  style={{
                    boxShadow:
                      "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                  }}
                  name="categoryName" // Fixed: Changed from "name" to "categoryName" to match state
                  value={filters.categoryName}
                  onChange={handleFilterChange}
                  className="form-select dropdown_input"
                >
                  <option value="">--Select--</option>
                  {categoriesList
                  .filter((item) => item.isActive)
                  .map((item) => (
                    <option value={item.categoryName} key={item.categoryName}>
                      {item.categoryName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-3 mb-3 text-start">
              <button
                className="btn px-4"
                onClick={handleSearch}
                style={{
                  background: "#a7252ced",
                  border: "none",
                  color: "white",
                  boxShadow:
                    "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                }}
              >
                SEARCH
              </button>
            </div>
          </div>

          <div className="d-flex justify-content-between mt-1 mb-1">
            <button
              className="btn"
              onClick={downloadExcel}
              style={{
                background: "#9CA3AF",
                color: "white",
                boxShadow:
                  "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
              }}
            >
              Excel <i className="fa-solid fa-download ms-1"></i>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleBulkUpload}
              style={{ display: "none" }} // Hide the input
            />
            <button
              onClick={handleButtonClick}
              style={{
                background: "#9CA3AF",
                color: "white",
                border: "none",
                borderRadius: "3px",
                boxShadow:
                  "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
              }}
            >
              Upload Bulk File <i className="fa-solid fa-upload ms-1"></i>
            </button>
          </div>

          <div className="row mb-3">
            <div className="col-md-12">
              <div className="table-responsive">
                <div
                  className="ag-theme-alpine"
                  style={{ height: 546.5, width: "100%", margin: "auto" }}
                >
                  <AgGridReact
                    defaultColDef={defaultColDef}
                    rowData={filteredData}
                    columnDefs={columnDefs}
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={[10, 20, 50]}
                    rowHeight={45}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <ToastContainer />
      </main>
    </>
  );
}

export default SubCategory;
